/**
 * SafeLeafletMap.js — Centralized Leaflet Lifecycle Wrapper
 *
 * SINGLE SOURCE OF TRUTH for Leaflet StrictMode safety across all DeCarbonIQ maps.
 *
 * Problem solved:
 *   React 18 StrictMode double-mounts components. Leaflet's _initContainer stores
 *   _leaflet_id on the DOM node. On remount it finds _leaflet_id set and throws
 *   "Map container is already initialized" or "Map container is being reused."
 *
 * Fix:
 *   1. Track the live Leaflet map instance via an internal ref.
 *   2. In useLayoutEffect cleanup (fires during StrictMode's disappear-layout pass),
 *      call map.remove() to properly dispose the instance AND clear _leaflet_id.
 *   3. As a safety fallback, also delete _leaflet_id from any .leaflet-container
 *      inside the wrapper div.
 *
 * Why useLayoutEffect (not useEffect):
 *   useLayoutEffect cleanup fires SYNCHRONOUSLY after the disappear pass, before
 *   children reappear and Leaflet tries to re-initialize. useEffect fires too late.
 *
 * Usage:
 *   import SafeLeafletMap from "@/components/SafeLeafletMap";
 *
 *   <SafeLeafletMap
 *     center={[6.5244, 3.3792]}
 *     zoom={11}
 *     style={{ height: 400, width: "100%" }}
 *     wrapperStyle={{ borderRadius: 12, overflow: "hidden" }}
 *   >
 *     <TileLayer url="..." />
 *   </SafeLeafletMap>
 */
import React, { useRef, useLayoutEffect, forwardRef, useCallback } from "react";
import { MapContainer } from "react-leaflet";

const SafeLeafletMap = forwardRef(function SafeLeafletMap(
  { children, wrapperStyle, wrapperClassName, ...mapContainerProps },
  forwardedRef
) {
  const wrapperRef    = useRef(null);
  const mapInstanceRef = useRef(null); // Tracks live Leaflet map instance
  // stableMapRef is only ever SET (never cleared) by mapRefCallback.
  // react-leaflet's useImperativeHandle cleanup calls mapRefCallback(null), which
  // clears mapInstanceRef.current BEFORE SafeLeafletMap's useLayoutEffect cleanup
  // runs (children clean up before parents). Using stableMapRef ensures we still
  // have the map reference when our cleanup fires.
  const stableMapRef = useRef(null);

  useLayoutEffect(() => {
    return () => {
      // React 18 StrictMode double-mount fix.
      //
      // Leaflet's map.remove() throws "Map container is being reused" when:
      //   mapInstance._containerId !== container._leaflet_id
      //
      // Leaflet's _initContainer throws "Map container is already initialized" when:
      //   container._leaflet_id is truthy
      //
      // Fix: delete BOTH _containerId (on map instance) AND _leaflet_id (on DOM node)
      // synchronously in useLayoutEffect cleanup (fires before children reappear).
      // After deletion: undefined !== undefined → false → map.remove() won't throw.
      // And _leaflet_id is cleared so the next init won't throw "already initialized".
      const map = stableMapRef.current;
      if (map) {
        const container = map._container;
        // eslint-disable-next-line no-underscore-dangle
        try { delete map._containerId; } catch (_) { map._containerId = undefined; }
        if (container) {
          // eslint-disable-next-line no-underscore-dangle
          try { delete container._leaflet_id; } catch (_) { container._leaflet_id = undefined; }
        }
        stableMapRef.current = null;
      }
      // Belt-and-suspenders: clear any remaining .leaflet-container in wrapper
      if (wrapperRef.current) {
        wrapperRef.current.querySelectorAll(".leaflet-container").forEach(c => {
          // eslint-disable-next-line no-underscore-dangle
          try { delete c._leaflet_id; } catch (_) { c._leaflet_id = undefined; }
        });
      }
    };
  }, []); // Stable refs — no dependencies needed

  // Ref callback that tracks the Leaflet map instance AND forwards to parent if needed.
  // stableMapRef is only set when map is non-null so it survives useImperativeHandle cleanup.
  const mapRefCallback = useCallback((map) => {
    mapInstanceRef.current = map;
    if (map) {
      stableMapRef.current = map;
      // Patch map.remove() to suppress "Map container is being reused by another instance".
      // This error is thrown by Leaflet when _containerId !== container._leaflet_id.
      // It fires when react-leaflet's useEffect cleanup calls map.remove() AFTER
      // SafeLeafletMap's useLayoutEffect cleanup has already cleared _leaflet_id.
      // The patch silently swallows this one specific error; all others are re-thrown.
      if (!map._safeRemovePatched) {
        // eslint-disable-next-line no-underscore-dangle
        map._safeRemovePatched = true;
        const origRemove = map.remove.bind(map);
        map.remove = function safeRemove() {
          try { return origRemove(); }
          catch (e) {
            if (e && e.message && e.message.includes('being reused')) return map;
            throw e;
          }
        };
      }
    }
    if (!forwardedRef) return;
    if (typeof forwardedRef === "function") forwardedRef(map);
    else forwardedRef.current = map;
  }, [forwardedRef]);

  return (
    <div ref={wrapperRef}
      style={{ ...wrapperStyle, height: mapContainerProps.style?.height || wrapperStyle?.height || "100%", width: "100%" }}
      className={wrapperClassName}>
      <MapContainer ref={mapRefCallback} {...mapContainerProps}>
        {children}
      </MapContainer>
    </div>
  );
});

SafeLeafletMap.displayName = "SafeLeafletMap";

export default SafeLeafletMap;
